import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/constants/site";

const LINKS = [
  ["About", "#about"],
  ["Services", "#services"],
  ["Sectors", "#sectors"],
  ["Process", "#process"],
  ["Founder", "#founder"],
  ["Gallery", "#gallery"],
  ["Contact", "#contact"],
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="main-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "border-b border-gold/20 bg-royal/85 backdrop-blur-lg"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <a href="#hero" onClick={(e) => go(e, "#hero")} className="flex items-center gap-3" data-testid="nav-brand">
          <img
            src={LOGO_URL}
            alt="Rayasthaila logo"
            className="h-10 w-10 rounded-full bg-white object-cover p-0.5 ring-1 ring-gold/50"
          />
          <span className="font-display text-base font-semibold tracking-[0.28em] text-white sm:text-lg">RAYASTHAILA</span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map(([label, href]) => (
            <a
              key={href}
              href={href}
              onClick={(e) => go(e, href)}
              data-testid={`nav-link-${label.toLowerCase()}`}
              className="text-[11px] uppercase tracking-[0.22em] text-slate-300 transition-colors duration-300 hover:text-gold"
            >
              {label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => go(e, "#contact")}
            data-testid="nav-enquire-btn"
            className="rounded-full border border-gold/60 px-5 py-2 text-[11px] uppercase tracking-[0.22em] text-gold transition-colors duration-300 hover:bg-gold hover:text-royal-deep"
          >
            Enquire Now
          </a>
        </nav>

        <button
          className="text-white lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle navigation menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          className="flex flex-col gap-5 border-t border-gold/10 bg-royal/95 px-6 py-6 backdrop-blur-lg lg:hidden"
          data-testid="nav-mobile-menu"
        >
          {LINKS.map(([label, href]) => (
            <a
              key={href}
              href={href}
              onClick={(e) => go(e, href)}
              data-testid={`nav-mobile-link-${label.toLowerCase()}`}
              className="text-xs uppercase tracking-[0.22em] text-slate-200 transition-colors hover:text-gold"
            >
              {label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => go(e, "#contact")}
            data-testid="nav-mobile-enquire-btn"
            className="w-fit rounded-full border border-gold/60 px-5 py-2 text-[11px] uppercase tracking-[0.22em] text-gold"
          >
            Enquire Now
          </a>
        </div>
      )}
    </motion.header>
  );
};
